import * as Card from '@/components/ui/card'
import { debounce } from '@solid-primitives/scheduled'
import type Quill from 'quill'
import { type Component, Suspense, createEffect, createSignal, lazy, onCleanup } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Box, Grid } from 'styled-system/jsx'
import { useUpsert } from '../state'
import { useMention } from '../state/useMention'
import { usePresence } from '../state/usePresence'
import ReplyingTo from './ReplyingTo'
import Toolbar from './Toolbar'
import Voice from './Voice'
import './input.css'
import { formats } from './quillFormatting'

import { Text } from '@/components/ui/text'
import { Loading } from '@components'
import { Avatar } from '@features/Avatar'
import type { Tables } from '@hvf/contracts'

export type ProfileMentionItem = Tables<'profile'> & {
  id: string
  value: string
}

const LazyQuillEditor = lazy(() => import('./QuillEditor'))

export const Send: Component<{ currentChannelId?: string }> = (props) => {
  const { send, editingMessage, replyMessage } = useService(useUpsert)()
  const { profilesQuery, setSearchTerm, setActiveMentions } = useService(useMention)()
  const { startTyping, stopTyping } = useService(usePresence)()

  const [isVoiceOpen, setIsVoiceOpen] = createSignal(false)
  let quill: Quill

  let isBroadcastingTyping = false

  // ✅ 1. Strongly type the pending request object
  let pendingMentionRequest: {
    renderList: (items: ProfileMentionItem[], searchTerm: string) => void
    searchTerm: string
  } | null = null

  createEffect(() => {
    const isFetching = profilesQuery.isFetching
    const data: Tables<'profile'>[] = profilesQuery.data || []

    if (!isFetching && pendingMentionRequest) {
      const { renderList, searchTerm } = pendingMentionRequest

      const mappedProfiles: ProfileMentionItem[] = data.map((p) => ({
        ...p,
        id: p.id,
        value: p.username ?? 'Unknown User',
      }))

      renderList(mappedProfiles, searchTerm)
    }
  })

  const handleStopTyping = debounce(() => {
    if (isBroadcastingTyping) {
      isBroadcastingTyping = false
      stopTyping()
    }
  }, 2000)

  onCleanup(() => {
    if (isBroadcastingTyping) stopTyping()
    handleStopTyping.clear()
  })

  createEffect(() => {
    const message = editingMessage()
    if (message && quill) {
      quill.root.innerHTML = message.content
    }
  })

  const onSend = () => {
    const sanitizeHTML = (html: string) => {
      let sanitized = html
        .replace(/&nbsp;/g, ' ')
        .replace(/<(p|div)><br><\/\1>/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim()
      if (!sanitized.startsWith('<') && sanitized !== '') sanitized = `<p>${sanitized}</p>`
      return sanitized
    }

    if (!quill) return
    quill.update()

    const rawHTML = quill.getSemanticHTML()
    const cleanHTML = sanitizeHTML(rawHTML)

    if (props.currentChannelId && cleanHTML !== '<p></p>' && cleanHTML !== '') {
      send(cleanHTML, props.currentChannelId)
      quill.setText('')

      if (isBroadcastingTyping) {
        isBroadcastingTyping = false
        stopTyping()
        handleStopTyping.clear()
      }
    }
  }

  const attachAudioToMessage = (audioUrl: string) => {
    if (!quill) return
    const range = quill.getSelection(true)
    const insertIndex = range ? range.index : quill.getLength()

    quill.insertText(insertIndex, '\n')
    quill.insertEmbed(insertIndex + 1, 'audio', audioUrl)
    quill.insertText(insertIndex + 2, '\n')
    setIsVoiceOpen(false)
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (!isBroadcastingTyping) {
      isBroadcastingTyping = true
      startTyping()
    }
    handleStopTyping()

    if (e.ctrlKey && e.key === 'Enter') {
      e.preventDefault()
      onSend()
    }
  }

  return (
    <Grid
      gridTemplateRows={replyMessage() ? 'auto 1fr' : '1fr'}
      gap={0}
      mt={2}
    >
      <ReplyingTo />
      <Card.Root
        p="12px"
        border="none"
        borderTop="1px solid var(--colors-editorial-hair)"
        borderRadius="0"
        boxShadow="none"
        bg="editorialSoft"
      >
        <Voice
          setAudio={attachAudioToMessage}
          isOpen={isVoiceOpen}
          setIsOpen={setIsVoiceOpen}
        />
        <Box
          class={css({
            display: 'grid',
            gridTemplateRows: 'auto 1fr',
            height: '100%',
            gap: 0,
            position: 'relative',
            border: '1px solid var(--colors-editorial-hair-strong)',
            borderRadius: '12px',
            background: 'var(--colors-editorial-soft)',
            overflow: 'hidden',
            _focusWithin: { borderColor: 'var(--colors-editorial-accent)' },
          })}
        >
          <Toolbar onSend={onSend} />
          <Grid
            gap={0}
            overflow={'auto'}
            maxHeight='70vh'
          >
            <Suspense fallback={<Loading />}>
              <LazyQuillEditor
                onInstance={(el) => (quill = el)}
                theme="snow"
                onKeyDown={onKeyDown}
                formats={formats}
                modules={{
                  toolbar: {
                    container: '#custom-toolbar',
                    handlers: {
                      undo: () => setIsVoiceOpen(true),
                    },
                  },
                  mention: {
                    allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
                    mentionDenotationChars: ["@"],
                    positioningStrategy: 'fixed',
                    mentionContainerClass: `ql-mention-list-container ${css({ zIndex: 9999 })}`,
                    source: (
                      searchTerm: string,
                      renderList: (items: ProfileMentionItem[], searchTerm: string) => void
                    ) => {
                      setSearchTerm(searchTerm)
                      pendingMentionRequest = { renderList, searchTerm }
                    },
                    onSelect: (
                      item: ProfileMentionItem,
                      insertItem: (item: ProfileMentionItem) => void
                    ) => {
                      setActiveMentions((prev) => [...prev, item])
                      insertItem(item)
                    },
                    renderItem: (item: ProfileMentionItem) => {
                      return <div class={css({
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      })}>
                        <Avatar id={item.id} />
                        <Text>{item.username}</Text>
                      </div>
                    }
                  }
                }}
              />
            </Suspense>
          </Grid>
        </Box>
      </Card.Root>
    </Grid>
  )
}
